<template>
  <div class="app-layout" :class="{ 'app-layout--collapsed': sidebarCollapsed }">
    <AppSidebar
      :collapsed="sidebarCollapsed"
      :groups="visibleGroups"
      @toggle="toggleSidebar"
      @logout="logout"
    />
    <div class="app-layout__workspace">
      <AppTopbar
        :collapsed="sidebarCollapsed"
        :user="currentUser"
        @toggle="toggleSidebar"
        @logout="logout"
      />
      <MobileNavigation :groups="visibleGroups" :user="currentUser" @logout="logout" />
      <main class="app-layout__content">
        <div v-if="showBreadcrumbs" class="app-layout__breadcrumbs container-fluid">
          <AppBreadcrumbs />
        </div>
        <slot />
      </main>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import AppSidebar from '@/components/AppSidebar.vue'
import AppTopbar from '@/components/AppTopbar.vue'
import MobileNavigation from '@/components/MobileNavigation.vue'
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'
import { navigationGroups, type NavigationGroup, type NavigationItem } from '@/config/navigation'
import { resolveBreadcrumbs } from '@/router/breadcrumbs'
import type { AuthUser } from '@/store'

const store = useStore()
const router = useRouter()
const route = useRoute()
const sidebarCollapsed = ref(localStorage.getItem('docbrain_sidebar_collapsed') === 'true')
const currentUser = computed(() => store.getters.currentUser as AuthUser | null)
const showBreadcrumbs = computed(() => resolveBreadcrumbs(route).length > 0)

const hasPermission = (entry: NavigationGroup | NavigationItem) => {
  if (entry.roles?.length && !entry.roles.includes(currentUser.value?.role || '')) return false
  if (entry.module && !store.getters.hasModule(entry.module)) return false
  if (entry.permission === 'contabilidad' && !store.getters.hasContabilidadModule) return false
  if (entry.permission === 'amortizaciones' && !store.getters.canAccessAmortizaciones) return false
  return true
}

const visibleGroups = computed<NavigationGroup[]>(() =>
  navigationGroups
    .filter(hasPermission)
    .map((group) => ({ ...group, items: group.items.filter(hasPermission) }))
    .filter((group) => group.items.length > 0 || group.showWhenEmpty)
)

const toggleSidebar = () => {
  sidebarCollapsed.value = !sidebarCollapsed.value
  localStorage.setItem('docbrain_sidebar_collapsed', String(sidebarCollapsed.value))
}
const logout = () => {
  store.commit('clearAuth')
  router.push('/login')
}
</script>
